import { makeStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import SelectFieldItem from '@sprinx/react-mui-fields/SelectFieldItem';
import SelectField from '@sprinx/react-mui-fields/SelectField';
import kebabCase from 'lodash/kebabCase';
import { catalogueFilterState } from '../../../api/products/catalogue';
import { Manufacturer } from '../../../api/products/productManufacturers';
import { mainNavigationMatchState } from '../../../api/mainNavigation';
import { activeManufacturersFilter } from '../../../config';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import { useAdminConfig } from '../../../adminConfig';
import CataloguePageListToolbarViewSwitcher from './CataloguePageListToolbarViewSwitcher';
import CataloguePageListToolbarOrderingPicker from './CataloguePageListToolbarOrderingPicker';

export type CataloguePageListToolbarProps = StandardDivProps<CataloguePageListToolbarClassKey>;

export type CataloguePageListToolbarClassKey = 'root';

const themeSettings = { name: 'CataloguePageListToolbar' };
const useStyles = makeStyles(
  (theme) => ({
    root: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 1),
      },
      '@media(max-width:450px)': {
        flexDirection: 'column',
        alignItems: 'stretch',
      },
      gap: '0.5rem',
      // '&..MuiToggleButtonGroup-groupedHorizontal:not(:first-child)': {
      //   border: '1px solid #0C2F59',
      // },
    },
    ordering: {
      flexGrow: 1,
      margin: theme.spacing(0.5, 0),
    },
    viewActions: {
      margin: theme.spacing(0.5, 0, 0.5, 0.5),
      '@media(max-width:450px)': {
        alignSelf: 'flex-end',
      },
    },
  }),
  themeSettings,
);

const CataloguePageListToolbar = React.forwardRef<HTMLDivElement, CataloguePageListToolbarProps>(
  ({ className, classes: pClasses }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const t = useTranslateWithFallback();
    const defaultManufacturer: Manufacturer = useMemo(
      () => ({ name: t('cataloguePage/chooseManufacturer'), taxonomies: [], taxonomy: '_default' }),
      [t],
    );
    const [selectedManufacturer, setSelectedManufacturer] = useState<string>(defaultManufacturer.name);
    const [category, setCategory] = useState<string | undefined>(undefined);
    const mainNavigationMatchStateValue = useRecoilValue(mainNavigationMatchState);
    const [filter, setFilter] = useRecoilState(catalogueFilterState);

    const handleManufacturerChange = (manufacturer: string | undefined) => {
      setSelectedManufacturer(manufacturer || defaultManufacturer.name);
      setCategory(mainNavigationMatchStateValue.params.categories);
      setFilter({
        searchText: undefined,
        extraTaxonomies:
          manufacturer !== defaultManufacturer.name ? [`/manufacturer/${kebabCase(manufacturer)}`] : undefined,
      });
    };
    const { manufacturers: rawManufacturers } = useAdminConfig();

    const manufacturers = useMemo(() => {
      if (activeManufacturersFilter !== true) {
        return [defaultManufacturer];
      }
      if (!rawManufacturers) return [defaultManufacturer];
      return [
        defaultManufacturer,
        ...rawManufacturers.filter((m) => m.taxonomies.includes(mainNavigationMatchStateValue.url.slice(3))),
      ];
    }, [defaultManufacturer, mainNavigationMatchStateValue.url, rawManufacturers]);

    useEffect(() => {
      if (mainNavigationMatchStateValue.params.categories !== category) {
        setSelectedManufacturer(defaultManufacturer.name);
        setCategory(mainNavigationMatchStateValue.params.categories);
      } else if (
        selectedManufacturer === defaultManufacturer.name &&
        filter.extraTaxonomies &&
        filter.extraTaxonomies.length > 0
      ) {
        const filteredManufacturer = manufacturers.find((mt) => (filter.extraTaxonomies || []).includes(mt.taxonomy));
        if (filteredManufacturer) {
          setSelectedManufacturer(filteredManufacturer.name);
        }
      }
    }, [
      defaultManufacturer.name,
      mainNavigationMatchStateValue.params.categories,
      category,
      selectedManufacturer,
      filter.extraTaxonomies,
      manufacturers,
    ]);

    return (
      <Toolbar ref={ref} className={clsx(classes.root, className)}>
        <CataloguePageListToolbarOrderingPicker className={classes.ordering} />
        {activeManufacturersFilter && (
          <div>
            <SelectField<string>
              ref={ref}
              fullWidth
              value={selectedManufacturer}
              className={classes.root}
              onChange={handleManufacturerChange}
            >
              {manufacturers.map((m, idx) => (
                <SelectFieldItem key={idx} value={m.name}>
                  {m.name}
                </SelectFieldItem>
              ))}
            </SelectField>
          </div>
        )}
        <CataloguePageListToolbarViewSwitcher className={classes.viewActions} />
      </Toolbar>
    );
  },
);

CataloguePageListToolbar.displayName = 'CataloguePageListToolbar';

export default withThemeProps(themeSettings)(CataloguePageListToolbar);
